/**
 * Generic fetch-and-decode dedup cache: `load(path)` fetches+decodes at most once per path, no
 * matter how many overlapping callers ask for it concurrently (a burst of gunfire one-shots all
 * asking for the same cue before the first decode has resolved, e.g.). Kept free of `fetch`/
 * `AudioContext` so it's unit-testable with mocked async functions — `engine.ts` is the only
 * caller that plugs in the real ones.
 */

export interface BufferLoader<TBytes, TBuffer> {
  fetchBytes(path: string): Promise<TBytes>;
  decode(bytes: TBytes): Promise<TBuffer>;
}

export interface BufferCache<TBuffer> {
  /** Resolves to the same in-flight/resolved promise for a path already loading/loaded. */
  load(path: string): Promise<TBuffer>;
  has(path: string): boolean;
  size(): number;
}

/**
 * A failed load is *not* cached — its entry is removed on rejection, so a transient network
 * failure doesn't permanently poison a cue for the rest of the session; the next `load()` call
 * for that path starts a fresh attempt.
 */
export function createBufferCache<TBytes, TBuffer>(loader: BufferLoader<TBytes, TBuffer>): BufferCache<TBuffer> {
  const cache = new Map<string, Promise<TBuffer>>();

  function load(path: string): Promise<TBuffer> {
    const existing = cache.get(path);
    if (existing) return existing;
    const promise = loader.fetchBytes(path).then((bytes) => loader.decode(bytes));
    cache.set(path, promise);
    promise.catch(() => cache.delete(path));
    return promise;
  }

  return {
    load,
    has: (path) => cache.has(path),
    size: () => cache.size,
  };
}
